import { useState, useRef, useEffect, useContext } from 'react'
import { Button } from '../../Button'
import { GlassHoverEffect } from '../../GlassHoverEffect'
import { 
  HiChevronDown, 
  HiOutlineBuildingOffice,
  HiArrowsRightLeft
} from 'react-icons/hi2'
import { AuthContext } from '../../../contexts'
import { useAuthStore } from '../../../store/auth'
import { useMeusSetores, useSetorLogado } from '../../../views/demands/hooks/useData'
import type { Setor } from '../../../models'
import { toast } from '../../Toast'
import { useNavigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '../../../utils/formatter'
import { useEventEmitter } from '../../../hooks/useEventBus'
import { LoadingInline } from '../../Loading'
import { useKeyboardShortcutsContext } from '../../../contexts/KeyboardShortcutsContext'

const SectorSelector = () => {
  // Todos os hooks devem ser chamados incondicionalmente no topo do componente
  const auth = useContext(AuthContext);
  const { setUser } = useAuthStore()
  const { emit } = useEventEmitter()
  const [showSectorMenu, setShowSectorMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const { setores, loading: setoresLoading } = useMeusSetores()
  const { definirSetor, loading: definindoSetor } = useSetorLogado()
  const navigate = useNavigate()
  const { enabled: shortcutsEnabled } = useKeyboardShortcutsContext()
  
  // Extrair user do auth (pode ser null se auth não existir)
  const user = auth?.user;

  // Derivar setor logado preferindo o objeto completo; se ausente, usar a lista de setores pelo FK
  const setorLogado: Setor | undefined =
    user?.setor_logado ??
    (user?.setor_logado_fk
      ? setores.find((s) => s.id === user.setor_logado_fk)
      : undefined);

  // Keyboard shortcuts for sector switching (Ctrl+1 through Ctrl+9)
  useEffect(() => {
    if (!auth || !shortcutsEnabled || setoresLoading || setores.length === 0) return;

    const shortcuts: Array<{ keys: string; sectorIndex: number }> = [];
    
    // Create shortcuts for up to 9 sectors
    for (let i = 1; i <= Math.min(setores.length, 9); i++) {
      shortcuts.push({
        keys: `ctrl+${i}`,
        sectorIndex: i - 1
      });
    }

    // Register shortcuts
    const cleanupFunctions: Array<() => void> = [];

    shortcuts.forEach(({ sectorIndex }) => {
      const sector = setores[sectorIndex];
      if (!sector) return;

      const handleShortcut = () => {
        // Don't switch if already on this sector
        if (setorLogado?.id === sector.id) return;
        
        // Don't switch if currently changing sectors
        if (definindoSetor) return;

        handleSetorChange(sector);
      };

      // Use a simple event listener approach for dynamic shortcuts
      const keydownHandler = (event: KeyboardEvent) => {
        // Check if user is typing in an input
        const activeElement = document.activeElement;
        if (activeElement && ['input', 'textarea', 'select'].includes(activeElement.tagName.toLowerCase())) {
          return;
        }
        
        // Check for Quill editor
        if (activeElement && activeElement.closest('.ql-editor')) {
          return;
        }

        // Check for contenteditable
        if (activeElement && activeElement.getAttribute('contenteditable') === 'true') {
          return;
        }

        const isAltPressed = event.altKey;
        const keyNumber = event.key;

        if (isAltPressed && keyNumber === sectorIndex.toString()) {
          event.preventDefault(); // Prevent browser tab switching
          event.stopPropagation(); // Stop event bubbling
          handleShortcut();
        }
      };

      document.addEventListener('keydown', keydownHandler);
      cleanupFunctions.push(() => {
        document.removeEventListener('keydown', keydownHandler);
      });
    });

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcutsEnabled, setores, setoresLoading, setorLogado, definindoSetor]);

  // Close menu when clicking outside (but not while changing sectors)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Don't close menu if we're in the middle of changing sectors
      if (definindoSetor) return
      
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowSectorMenu(false)
      }
    }

    if (showSectorMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showSectorMenu, definindoSetor])

  // Proteção contra uso fora do AuthProvider (após todos os hooks)
  if (!auth) {
    console.warn('SectorSelector: AuthContext não encontrado, componente será omitido');
    return null;
  }

  const handleSetorChange = async (setor: Setor) => {
    try {
      // Don't close menu immediately - keep it open to show loading state
      const oldSectorId = setorLogado?.id;
      const response = await definirSetor(setor.id)

      // Alguns endpoints /user não retornam o objeto setor_logado ou setores.
      // Garantir que o store tenha ambos: objeto e FK do setor selecionado.
      // Preservar os setores existentes com pivot caso o backend não retorne.
      const updatedUser = {
        ...response.user,
        setor_logado: response.setor_logado ?? setor,
        setor_logado_fk: (response.setor_logado ?? setor).id,
        // Preservar setores com pivot se o response não tiver (fallback para array vazio)
        setores: response.user.setores ?? user?.setores ?? [],
      }
      setUser(updatedUser)
      // Persistir também no authService/localStorage para sobreviver ao reload
      try {
        const { authService } = await import('../../../services/auth')
        authService.saveUser(updatedUser)
      } catch {
        // noop - fallback já é o zustand persist
      }
      
      // Emitir evento de mudança de setor
      emit('sector-changed', {
        ...(oldSectorId !== undefined && { oldSectorId }),
        newSectorId: setor.id,
        user: updatedUser,
      });
      
      toast.success(`Setor alterado para: ${setor.nome_setor}`)
      
      // Close menu only after successful operation
      setShowSectorMenu(false)
      navigate('/')
      
    } catch (error) {
      toast.error('Erro ao alterar setor. Tente novamente.')
      console.error('Erro ao alterar setor:', error)
      // Close menu on error as well
      setShowSectorMenu(false)
    }
  }

  // Get the active index for GlassHoverEffect
  const getActiveIndex = () => {
    return setores.findIndex(setor => setor.id === setorLogado?.id)
  }

  return (
    <div className="relative" ref={menuRef}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowSectorMenu(!showSectorMenu)}
        className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 h-9 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 sm:h-6 sm:w-6 rounded-md bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
            <HiOutlineBuildingOffice className="w-4 h-4 sm:w-3 sm:h-3 text-white" />
          </div>
          {/* Texto oculto em mobile */}
          <div className="hidden sm:block text-left">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[100px] md:max-w-[120px]">
              {setorLogado ? setorLogado.nome_setor : 'Selecionar Setor'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {setores.length > 1 ? `${setores.length} setores` : 'Setor atual'}
            </p>
          </div>
        </div>
        <HiChevronDown className={cn(
          "w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 hidden sm:block",
          showSectorMenu && "rotate-180"
        )} />
      </Button>

      <AnimatePresence>
        {showSectorMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className={cn(
              "absolute top-full mt-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl shadow-2xl z-[9999] overflow-hidden",
              // Mobile: centralizado na tela
              "fixed left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)]",
              // Desktop: posicionado à direita do botão
              "sm:fixed sm:left-auto sm:translate-x-0 sm:right-4 sm:w-64",
              "md:absolute md:right-0 md:w-64"
            )}
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700">
              <div className="flex items-center gap-2">
                {definindoSetor ? (
                  <LoadingInline size="xs" />
                ) : (
                  <HiArrowsRightLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                )}
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {definindoSetor ? 'Alterando setor...' : 'Alterar Setor'}
                </span>
              </div>
            </div>

            {/* Setores List with GlassHoverEffect */}
            <div className={cn(
              "max-h-60 overflow-y-auto p-2 relative",
              definindoSetor && "pointer-events-none opacity-75"
            )}>
              {!setoresLoading && setores.length > 0 ? (
                <GlassHoverEffect
                  activeIndex={getActiveIndex()}
                  direction="vertical"
                  className="flex flex-col gap-1 relative"
                  glassClassName="rounded-lg shadow-sm bg-blue-50/90 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-700/40 backdrop-blur-sm"
                >
                  {({ containerRef, containerProps, getItemProps }) => (
                    <div 
                      ref={containerRef as React.RefObject<HTMLDivElement>}
                      className="flex flex-col gap-1 relative"
                      {...containerProps}
                    >
                      {setores.map((setor, index) => (
                        <button
                          key={setor.id}
                          onClick={() => handleSetorChange(setor)}
                          disabled={definindoSetor}
                          {...getItemProps(index)}
                          className={cn(
                            "flex items-center w-full px-3 py-2.5 text-sm hover:bg-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed rounded-lg relative z-10",
                            setorLogado?.id === setor.id
                              ? "text-blue-700 dark:text-blue-400 font-medium"
                              : "text-gray-700 dark:text-gray-300"
                          )}
                        >
                          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mr-3 flex-shrink-0">
                            <HiOutlineBuildingOffice className="w-4 h-4 text-white" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="font-medium truncate text-gray-900 dark:text-gray-100">{setor.nome_setor}</p>
                            <p className="text-xs opacity-70 text-gray-600 dark:text-gray-400">
                              {setorLogado?.id === setor.id ? 'Setor atual' : 'Clique para alterar'}
                            </p>
                          </div>
                          {setorLogado?.id === setor.id && (
                            <div className="ml-2">
                              {definindoSetor && (
                                <LoadingInline size="xs" />
                              )}
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </GlassHoverEffect>
              ) : (
                <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                  {setoresLoading ? 'Carregando setores...' : 'Nenhum setor disponível'}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export { SectorSelector }
